import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { useStore } from 'context/store-context';
import {
  OrderEditingAppearance,
  OrderEditingSettings,
} from 'types/order-editing';
import { useOrderEditingWorkspace } from './useOrderEditingWorkspace';
import { FulfillmentProtection } from './FulfillmentProtection';
import { ItemEditPermissionsNotice } from './ItemEditPermissionsNotice';
import {
  ExpandedSettings,
  ReviewQueue,
  SupportCancellationSettings,
} from './ExpandedWorkspace';
import OrderEditingSettingsTabs, {
  OrderEditingPage,
  orderEditingPages,
} from './OrderEditingSettingsTabs';
import { normalizeOrderEditingSettings } from './expandedSettings';
import { OrderEditingActivityView } from './OrderEditingActivity';
import {
  cardClass,
  inputClass,
  Preview,
  SettingToggle,
  InstallationNotice,
} from './WorkspaceSections';

const textFields: Array<
  [
    keyof Omit<
      OrderEditingAppearance,
      'locale' | 'buttonVariant' | 'buttonTone'
    >,
    string,
  ]
> = [
  ['title', 'Widget title'],
  ['subtitle', 'Widget description'],
  ['editTitle', 'Address section title'],
  ['cancelTitle', 'Cancellation section title'],
  ['cancelDescription', 'Cancellation description'],
  ['saveLabel', 'Save button label'],
  ['cancelLabel', 'Cancel order button label'],
  ['confirmLabel', 'Confirm button label'],
  ['backLabel', 'Back button label'],
];

const reportingDate = (value?: string, end = false) => {
  if (typeof value !== 'string' || !value) return undefined;
  const date = new Date(new Date(value).getTime() - (end ? 1 : 0));
  return Number.isFinite(date.getTime()) ? date : undefined;
};

const OrderEditing = ({ page = 'rules' }: { page?: OrderEditingPage }) => {
  const pageDetails = orderEditingPages.find((item) => item.id === page)!;
  const isOperations = page === 'requests' || page === 'activity';
  const unavailable = page === 'shipping' || page === 'integrations';
  const { storeId } = useStore();
  const state = useOrderEditingWorkspace(storeId);
  const {
    workspace,
    draft,
    setDraft,
    loading,
    saving,
    checking,
    reviewing,
    configuring,
    acknowledging,
    error,
    notice,
    conflict,
    dirty,
  } = state;
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reviewDirty, setReviewDirty] = useState(false);
  const [fulfillmentDirty, setFulfillmentDirty] = useState(false);
  const periodStart = workspace?.usage.periodStart;
  const periodEnd = workspace?.usage.periodEnd;
  useEffect(() => {
    setFrom(reportingDate(periodStart)?.toISOString().slice(0, 10) || '');
    setTo(reportingDate(periodEnd, true)?.toISOString().slice(0, 10) || '');
  }, [periodStart, periodEnd, storeId]);
  const busy =
    saving ||
    loading ||
    !!checking ||
    !!reviewing ||
    configuring ||
    !!acknowledging;
  const update = (patch: Partial<OrderEditingSettings>) =>
    setDraft((previous) => previous && { ...previous, ...patch });
  const appearance = (patch: Partial<OrderEditingAppearance>) =>
    draft && update({ appearance: { ...draft.appearance, ...patch } });
  const preset = (flexible: boolean) =>
    update({
      deadlineMinutes: flexible ? 60 : 30,
      editShipping: true,
      cancelOrder: flexible,
      protectedOnly: !flexible,
      allowPoBoxes: flexible,
      verifyAddresses: true,
      allowUnverified: false,
      sameCountryOnly: true,
      ...(flexible ? {} : { guestAccess: false }),
    });
  const resetDefaults = () =>
    setDraft(
      (previous) =>
        previous &&
        normalizeOrderEditingSettings({
          ...previous,
          deadlineMinutes: 30,
          editShipping: false,
          cancelOrder: false,
          allowPoBoxes: false,
          verifyAddresses: false,
          allowUnverified: false,
          protectedOnly: true,
          sameCountryOnly: true,
          guestAccess: false,
          notifications: { address: true, cancellation: true },
          expanded: undefined,
          catalog: undefined,
        }),
    );
  const applyDates = () => {
    const start = new Date(`${from}T00:00:00.000Z`);
    const end = new Date(`${to}T00:00:00.000Z`);
    end.setUTCDate(end.getUTCDate() + 1);
    const days = (end.getTime() - start.getTime()) / 86400000;
    if (!Number.isFinite(days) || days < 1 || days > 93) {
      state.setError('Choose a date range of 1 to 93 days.');
      return;
    }
    void state.load(false, {
      periodStart: start.toISOString(),
      periodEnd: end.toISOString(),
    });
  };

  return (
    <div className="min-h-full bg-gray-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-5 text-gray-900">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
          <div>
            <h1 className="text-2xl font-semibold">Order Editing</h1>
            <p className="mt-1 font-medium text-gray-900">
              {pageDetails.label}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {pageDetails.description}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              to={
                isOperations
                  ? '/settings/order-settings/customization'
                  : '/order-editing/requests'
              }
            >
              {isOperations ? 'Order Editing settings' : 'View requests'}
            </Link>
            {workspace && draft && pageDetails.editable && !unavailable && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-600">
                  {dirty
                    ? 'Unsaved changes'
                    : `Saved version ${state.revision}`}
                </span>
                <Button
                  onClick={() => {
                    void state.save();
                  }}
                  disabled={busy || conflict}
                  isLoading={saving}
                >
                  Save settings
                </Button>
              </div>
            )}
          </div>
        </header>
        {!isOperations && <OrderEditingSettingsTabs page={page} />}
        {unavailable && (
          <section className={cardClass} aria-labelledby="unavailable-heading">
            <h2 id="unavailable-heading" className="text-base font-semibold">
              {page === 'shipping'
                ? 'Shipping Offers is paused'
                : 'Integrations are not available yet'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {page === 'shipping'
                ? 'Shipping Offers is disabled for now. Saved offers are kept for later.'
                : 'Shopify Flow and fulfillment connections are still being verified before release.'}
            </p>
            <Link
              to="/settings/order-settings/customization"
              className="mt-4 inline-block text-sm font-medium text-blue-700 underline"
            >
              Back to Customization
            </Link>
          </section>
        )}
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          >
            {error}
            {conflict && (
              <div className="mt-3">
                <Button
                  variant="outlined"
                  onClick={() => {
                    void state.load(true);
                  }}
                  disabled={busy}
                >
                  Reload latest settings
                </Button>
              </div>
            )}
          </div>
        )}
        {notice && (
          <p
            role="status"
            className="rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-800"
          >
            {notice}
          </p>
        )}
        {!storeId && (
          <p className={cardClass}>Select a store to manage Order Editing.</p>
        )}
        {storeId && !workspace && loading && (
          <p role="status" className={cardClass}>
            Loading Order Editing…
          </p>
        )}
        {storeId && !workspace && !loading && error && (
          <Button
            onClick={() => {
              void state.load(true);
            }}
          >
            Retry
          </Button>
        )}
        {workspace && draft && (
          <>
            <UnsavedChangesGuard
              hasUnsavedChanges={dirty || reviewDirty || fulfillmentDirty}
              onSave={state.save}
              saveBeforeLeaving={!reviewDirty && !fulfillmentDirty}
              message={
                reviewDirty
                  ? 'Your review notes or fulfillment confirmations have not been submitted. Continue reviewing or leave without keeping this draft.'
                  : fulfillmentDirty
                  ? 'Your fulfillment setting has not been saved. Save it on Editing Rules or leave without keeping this draft.'
                  : undefined
              }
            />
            {(page === 'customization' || page === 'rules') && (
              <ItemEditPermissionsNotice
                key={storeId}
                workspace={workspace}
                draft={draft}
                busy={busy}
                dirty={dirty || fulfillmentDirty}
                onRefresh={() => {
                  void state.load(false);
                }}
              />
            )}
            {!isOperations && !unavailable && (
              <InstallationNotice
                workspace={workspace}
                refreshing={busy}
                onRefresh={() => {
                  void state.load(false);
                }}
              />
            )}
            {page === 'rules' && (
              <>
                <section
                  id="order-edit-policy"
                  className={cardClass}
                  aria-labelledby="policy-heading"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 id="policy-heading" className="text-base font-semibold">
                      Editing policy
                    </h2>
                    <details className="text-sm">
                      <summary className="cursor-pointer font-medium text-gray-600">
                        Presets & reset
                      </summary>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          variant="outlined"
                          disabled={busy}
                          onClick={() => preset(false)}
                        >
                          Conservative preset
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={busy}
                          onClick={() => preset(true)}
                        >
                          Flexible preset
                        </Button>
                        <Button
                          variant="outlined"
                          disabled={busy}
                          onClick={resetDefaults}
                        >
                          Reset editing rules
                        </Button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Reset turns editing off. Your customization is kept.
                        Save to apply.
                      </p>
                    </details>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Choose which changes customers can make, and for how long.
                  </p>
                  <fieldset
                    disabled={busy}
                    className="mt-4 grid gap-x-8 md:grid-cols-2"
                  >
                    <div>
                      <label
                        htmlFor="editing-window"
                        className="text-sm font-medium"
                      >
                        Editing window (minutes)
                      </label>
                      <NonNegativeNumberInput
                        id="editing-window"
                        min="1"
                        max="14400"
                        step="1"
                        className={inputClass}
                        value={
                          Number.isFinite(draft.deadlineMinutes)
                            ? draft.deadlineMinutes
                            : ''
                        }
                        onChange={(event) =>
                          update({
                            deadlineMinutes:
                              event.target.value === ''
                                ? NaN
                                : Number(event.target.value),
                          })
                        }
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Starts when the order is placed; ends earlier if
                        fulfilled.
                      </p>
                      <SettingToggle
                        label="Allow shipping address changes"
                        value={draft.editShipping}
                        onChange={(editShipping) => update({ editShipping })}
                      />
                      <SettingToggle
                        label="Allow order cancellation"
                        value={draft.cancelOrder}
                        onChange={(cancelOrder) => update({ cancelOrder })}
                      />
                      <SettingToggle
                        label="Delivery Guarantee orders only"
                        value={draft.protectedOnly}
                        onChange={(protectedOnly) => update({ protectedOnly })}
                      />
                    </div>
                    <div>
                      <details className="rounded-md border border-gray-200 p-3">
                        <summary className="cursor-pointer text-sm font-medium">
                          Address options
                        </summary>
                        <SettingToggle
                          label="Keep the original destination country"
                          description="Keeps country-specific tax and duties unchanged."
                          value={draft.sameCountryOnly}
                          onChange={(sameCountryOnly) =>
                            update({ sameCountryOnly })
                          }
                        />
                        <SettingToggle
                          label="Allow PO boxes"
                          value={draft.allowPoBoxes}
                          onChange={(allowPoBoxes) => update({ allowPoBoxes })}
                        />
                        <SettingToggle
                          label="Check addresses with Google Maps"
                          description="Suggests matches; does not guarantee delivery."
                          value={draft.verifyAddresses}
                          onChange={(verifyAddresses) =>
                            update({ verifyAddresses })
                          }
                        />
                        <SettingToggle
                          label="Allow saving an unverified address"
                          description="The customer must confirm an unmatched address."
                          value={draft.allowUnverified}
                          onChange={(allowUnverified) =>
                            update({ allowUnverified })
                          }
                          disabled={!draft.verifyAddresses}
                        />
                      </details>
                      <SettingToggle
                        label="Allow verified guest access"
                        description={
                          workspace.setup.guestAccessReady
                            ? 'Verify with a code sent to the order email.'
                            : 'Complete guest verification setup first.'
                        }
                        value={draft.guestAccess}
                        onChange={(guestAccess) => update({ guestAccess })}
                        disabled={
                          !workspace.setup.guestAccessReady &&
                          !draft.guestAccess
                        }
                      />
                    </div>
                  </fieldset>
                  <details className="mt-5 border-t border-gray-200 pt-4">
                    <summary className="cursor-pointer text-sm font-semibold">
                      Notifications
                    </summary>
                    <div className="grid gap-x-8 md:grid-cols-2">
                      <SettingToggle
                        label="Address change notification"
                        description="Email your store contact after a completed change."
                        value={draft.notifications.address}
                        disabled={busy}
                        onChange={(address) =>
                          update({
                            notifications: { ...draft.notifications, address },
                          })
                        }
                      />
                      <SettingToggle
                        label="Cancellation notification"
                        description="Email your store contact after a confirmed cancellation."
                        value={draft.notifications.cancellation}
                        disabled={busy}
                        onChange={(cancellation) =>
                          update({
                            notifications: {
                              ...draft.notifications,
                              cancellation,
                            },
                          })
                        }
                      />
                    </div>
                  </details>
                </section>
                <ExpandedSettings
                  key={workspace.storeId}
                  draft={draft}
                  update={update}
                  setup={workspace.setup}
                  disabled={busy}
                  loadCatalog={state.loadCatalog}
                />
                <FulfillmentProtection
                  key={`fulfillment-${workspace.storeId}`}
                  integration={workspace.integration}
                  busy={busy}
                  onConfigure={state.configureIntegration}
                  onRefresh={async () => (await state.load(false))?.integration}
                  onDirtyChange={setFulfillmentDirty}
                />
                <SupportCancellationSettings
                  draft={draft}
                  update={update}
                  disabled={busy}
                />
              </>
            )}
            {page === 'customization' && (
              <section
                id="order-edit-appearance"
                className={cardClass}
                aria-labelledby="appearance-heading"
              >
                <h2 id="appearance-heading" className="text-base font-semibold">
                  Customer wording & appearance
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Preview your changes as you edit. Leave text blank to use
                  defaults.
                </p>
                <div className="mt-5 grid items-start gap-6 lg:grid-cols-2">
                  <div className="lg:sticky lg:top-40 lg:col-start-2 lg:row-start-1">
                    <Preview settings={draft} />
                  </div>
                  <fieldset
                    disabled={busy}
                    className="grid grid-cols-2 gap-4 lg:col-start-1 lg:row-start-1"
                  >
                    <label className="col-span-2 text-sm font-medium">
                      Customer language
                      <select
                        className={inputClass}
                        value={draft.appearance.locale}
                        onChange={(event) =>
                          appearance({
                            locale: event.target
                              .value as OrderEditingAppearance['locale'],
                          })
                        }
                      >
                        <option value="auto">Automatic</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                        <option value="fr">Français</option>
                      </select>
                    </label>
                    {textFields.slice(0, 2).map(([key, label]) => (
                      <label
                        key={key}
                        className="col-span-2 text-sm font-medium"
                      >
                        {label}
                        <input
                          className={inputClass}
                          maxLength={
                            key === 'subtitle' || key === 'cancelDescription'
                              ? 500
                              : 120
                          }
                          placeholder="Use translated default"
                          value={draft.appearance[key]}
                          onChange={(event) =>
                            appearance({ [key]: event.target.value })
                          }
                        />
                      </label>
                    ))}
                    <label className="text-sm font-medium">
                      Button style
                      <select
                        className={inputClass}
                        value={draft.appearance.buttonVariant}
                        onChange={(event) =>
                          appearance({
                            buttonVariant: event.target
                              .value as OrderEditingAppearance['buttonVariant'],
                          })
                        }
                      >
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                        <option value="plain">Plain</option>
                      </select>
                    </label>
                    <label className="text-sm font-medium">
                      Button tone
                      <select
                        className={inputClass}
                        value={draft.appearance.buttonTone}
                        onChange={(event) =>
                          appearance({
                            buttonTone: event.target
                              .value as OrderEditingAppearance['buttonTone'],
                          })
                        }
                      >
                        <option value="auto">Automatic</option>
                        <option value="critical">Critical</option>
                      </select>
                    </label>
                    <details className="col-span-2 border-t border-gray-200 pt-4">
                      <summary className="cursor-pointer text-sm font-medium">
                        More wording options
                      </summary>
                      <div className="mt-4 grid grid-cols-2 gap-4">
                        {textFields.slice(2).map(([key, label]) => (
                          <label
                            key={key}
                            className={`${
                              key === 'cancelDescription' ? 'col-span-2' : ''
                            } text-sm font-medium`}
                          >
                            {label}
                            <input
                              className={inputClass}
                              maxLength={
                                key === 'cancelDescription' ? 500 : 120
                              }
                              placeholder="Use translated default"
                              value={draft.appearance[key]}
                              onChange={(event) =>
                                appearance({ [key]: event.target.value })
                              }
                            />
                          </label>
                        ))}
                      </div>
                    </details>
                  </fieldset>
                </div>
              </section>
            )}
            {page === 'requests' && (
              <ReviewQueue
                key={storeId}
                requests={workspace.requests || []}
                busy={busy}
                reviewing={reviewing}
                onReview={state.reviewRequest}
                onDirtyChange={setReviewDirty}
              />
            )}
            {page === 'activity' && (
              <OrderEditingActivityView
                workspace={workspace}
                busy={busy}
                checking={checking}
                acknowledging={acknowledging}
                onReconcile={state.reconcile}
                onAcknowledgeRouting={state.acknowledgeRouting}
                periodControls={
                  <div className="mt-5 flex flex-wrap items-end gap-3">
                    <label className="text-sm font-medium">
                      From date
                      <input
                        type="date"
                        className={inputClass}
                        value={from}
                        onChange={(event) => setFrom(event.target.value)}
                      />
                    </label>
                    <label className="text-sm font-medium">
                      To date
                      <input
                        type="date"
                        className={inputClass}
                        value={to}
                        onChange={(event) => setTo(event.target.value)}
                      />
                    </label>
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={applyDates}
                    >
                      Apply dates
                    </Button>
                    <span className="pb-2 text-xs text-gray-500">
                      UTC · Up to 93 days
                    </span>
                  </div>
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrderEditing;
